import type { ColorMode, LiveTruck } from '@chukua/common';
import { LngLatBounds, Map as MapLibreMap, type GeoJSONSource, type MapLayerMouseEvent } from 'maplibre-gl';
import { useEffect, useRef, useState } from 'react';
import { configureMapLibreWorker } from '../../../shared/features/maps/adapters/configure-maplibre-worker';
import { basemapStyleUrl } from '../../../shared/features/maps/policies/basemap';
import { CORRIDOR_CENTER, CORRIDOR_ZOOM, ZONE_FIT_OPTIONS } from '../../../shared/features/maps/policies/map-framing';
import { selectionFilter, truckLayers, TRUCK_HIT_LAYER_ID, TRUCK_SELECTION_LAYER_ID, TRUCKS_SOURCE_ID } from '../adapters/truck-layers';
import { liveTruckFeatures } from '../policies/live-truck-features';

interface UseLiveMapArgs {
  /** Null while unknown or unset: no map is mounted. */
  tileKey: string | null;
  mode: ColorMode;
  /** The trucks to draw: already filtered. */
  trucks: LiveTruck[];
  now: Date;
  selectedTruckId: string | null;
  onSelect: (truckId: string) => void;
}

function truckIdAt(event: MapLayerMouseEvent): string | null {
  const truckId = event.features?.[0]?.properties?.truckId;
  return typeof truckId === 'string' ? truckId : null;
}

interface MountArgs {
  container: HTMLDivElement;
  tileKey: string;
  mode: ColorMode;
  onSelect: (truckId: string) => void;
  onLoad: (map: MapLibreMap) => void;
}

function mountLiveMap({ container, tileKey, mode, onSelect, onLoad }: MountArgs) {
  configureMapLibreWorker();
  const map = new MapLibreMap({ container, style: basemapStyleUrl(mode, tileKey), center: CORRIDOR_CENTER, zoom: CORRIDOR_ZOOM });
  map.on('load', () => {
    map.addSource(TRUCKS_SOURCE_ID, { type: 'geojson', data: liveTruckFeatures([], new Date()) });
    truckLayers(mode).forEach((layer) => map.addLayer(layer));
    onLoad(map);
  });
  map.on('mouseenter', TRUCK_HIT_LAYER_ID, () => {
    map.getCanvas().style.cursor = 'pointer';
  });
  map.on('mouseleave', TRUCK_HIT_LAYER_ID, () => {
    map.getCanvas().style.cursor = '';
  });
  map.on('click', TRUCK_HIT_LAYER_ID, (event) => {
    const truckId = truckIdAt(event);
    if (truckId) onSelect(truckId);
  });
  const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => map.resize());
  resizeObserver?.observe(container);
  return () => {
    resizeObserver?.disconnect();
    map.remove();
  };
}

function boundsOf(trucks: LiveTruck[]): LngLatBounds {
  const bounds = new LngLatBounds();
  trucks.forEach(({ fix }) => bounds.extend([fix.lng, fix.lat]));
  return bounds;
}

/**
 * The live map: the basemap with one dot per truck. The map is rebuilt when the colour mode changes (the basemap and every layer follow
 * it); the trucks are pushed into its source as they move, and the view frames them once, the first time there are any, so a later
 * pan or zoom by the person is never undone.
 */
export function useLiveMap({ tileKey, mode, trucks, now, selectedTruckId, onSelect }: UseLiveMapArgs) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [map, setMap] = useState<MapLibreMap | null>(null);
  const onSelectRef = useRef(onSelect);
  const hasFramedRef = useRef(false);

  useEffect(() => {
    onSelectRef.current = onSelect;
  });

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !tileKey) return;
    const destroy = mountLiveMap({ container, tileKey, mode, onSelect: (truckId) => onSelectRef.current(truckId), onLoad: setMap });
    return () => {
      setMap(null);
      destroy();
    };
  }, [tileKey, mode]);

  useEffect(() => {
    map?.getSource<GeoJSONSource>(TRUCKS_SOURCE_ID)?.setData(liveTruckFeatures(trucks, now));
  }, [map, trucks, now]);

  useEffect(() => {
    map?.setFilter(TRUCK_SELECTION_LAYER_ID, selectionFilter(selectedTruckId));
  }, [map, selectedTruckId]);

  useEffect(() => {
    if (!map || hasFramedRef.current || trucks.length === 0) return;
    hasFramedRef.current = true;
    map.fitBounds(boundsOf(trucks), { ...ZONE_FIT_OPTIONS, maxZoom: 12, duration: 0 });
  }, [map, trucks]);

  return { containerRef, map };
}
